import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";

const EMBLEM = "/brand/lockup_horizontal.png";
const STACKED = "/brand/logo_stacked_white.png";

const navItems = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/projects", label: "Projects" },
  { to: "/contact", label: "Enquiries" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${scrolled ? "backdrop-blur-md bg-[rgba(35,31,32,0.82)] border-b border-[color:var(--hm-line)]" : "bg-transparent"}`}
    >
      <div className="hm-container flex items-center justify-between py-5">
        <Link to="/" className="flex items-center" data-testid="nav-logo" aria-label="Har-monic — Home">
          <img src={EMBLEM} alt="Har-Monic" className="h-11 w-auto opacity-95" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.28em] font-medium transition-colors ${isActive ? "text-[color:var(--hm-pink)]" : "text-[color:var(--hm-ink-dim)] hover:text-[color:var(--hm-ink)]"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <button
          className="lg:hidden text-[color:var(--hm-ink)] p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="nav-toggle"
        >
          <span className="block w-6 h-[1.5px] bg-current mb-[6px]" />
          <span className="block w-6 h-[1.5px] bg-current mb-[6px]" />
          <span className="block w-4 h-[1.5px] bg-current ml-auto" />
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[color:var(--hm-line)] bg-[color:var(--hm-bg)]" data-testid="mobile-menu">
          <div className="hm-container py-6 flex flex-col gap-4">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `font-display text-2xl ${isActive ? "text-[color:var(--hm-pink)]" : "text-[color:var(--hm-ink)]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[color:var(--hm-line)] mt-32" data-testid="site-footer">
      <div className="hm-container py-20">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <img src={STACKED} alt="Har-Monic — Soulful Regeneration" className="h-32 w-auto mb-6 opacity-95" />
            <p className="text-[color:var(--hm-ink-dim)] max-w-sm text-[15px]">
              A London-based regenerative property development company. Heritage and vacant buildings restored into design-led residential, hospitality, and creative workspace projects.
            </p>
          </div>
          <div className="md:col-span-3">
            <p className="hm-eyebrow mb-5">Navigate</p>
            <ul className="space-y-2.5">
              {navItems.map((n) => (
                <li key={n.to}><Link to={n.to} className="text-[color:var(--hm-ink-dim)] hover:text-[color:var(--hm-ink)] text-[15px]">{n.label}</Link></li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <p className="hm-eyebrow mb-5">Office</p>
            <address className="not-italic text-[color:var(--hm-ink-dim)] leading-relaxed text-[15px]">
              Har-monic Development Ltd.<br />
              London, United Kingdom<br />
              <a href="mailto:hi@har-monic.com" className="hm-link mt-2 inline-block">hi@har-monic.com</a>
            </address>
          </div>
        </div>
        <div className="hm-rule mt-16 mb-6" />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[12px] text-[color:var(--hm-ink-dim)]">
          <span>© {new Date().getFullYear()} Har-monic Ltd. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="hm-grain">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
